import { Check } from 'lucide-react'
import { useState } from 'react'

export default function Pricing() {
	const [teamSeats, setTeamSeats] = useState(2)
	const [isYearly, setIsYearly] = useState(false)

	return (
		<div
			id="pricing"
			className="min-h-screen bg-white px-4 sm:px-6 lg:px-8 border-t border-gray-100 pt-8 sm:pt-10"
		>
			<div className="max-w-7xl mx-auto">
				{/* Header Section */}
				<div className="text-center mb-8 sm:mb-12">
					<h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 mb-4">
						Simple pricing
					</h1>
				</div>

				{/* Billing Toggle - Rounded Tab */}
				<div className="flex items-center justify-center mb-8">
					<div className="p-1 rounded-full flex w-[270px] shadow-sm bg-white border border-gray-200">
						<button
							onClick={() => setIsYearly(false)}
							className={`flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all ${
								!isYearly
									? 'bg-gray-200 text-gray-900 shadow-sm'
									: 'text-gray-600 hover:text-gray-900'
							}`}
						>
							Monthly
						</button>
						<button
							onClick={() => setIsYearly(true)}
							className={`flex-1 px-3 py-2 rounded-full text-sm font-medium transition-all ${
								isYearly
									? 'bg-gray-200 text-gray-900 shadow-sm'
									: 'text-gray-600 hover:text-gray-900'
							}`}
						>
							Yearly
							<span className="text-[10px] text-green-700 bg-green-100 rounded-full px-2 py-0.5 ml-1">-32%</span>
						</button>
					</div>
				</div>

				{/* Pricing Grid */}
				<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 max-w-7xl mx-auto">
					{/* Free Plan */}
					<div className="border border-gray-200 rounded-lg bg-white shadow-sm">
						<div className="p-4 sm:p-6 pb-2">
							<h3 className="text-lg sm:text-xl font-semibold text-gray-900">
								Free
							</h3>
							<div className="flex items-baseline">
								<span className="text-3xl sm:text-4xl font-bold text-gray-900">
									$0
								</span>
								<span className="text-sm sm:text-base text-gray-600 ml-1">
									/per month
								</span>
							</div>
							<p className="text-sm sm:text-base text-gray-600 mt-2">
								Essential tools, for free.
							</p>
						</div>

						<div className="px-4 sm:px-6 pb-4 sm:pb-6">
							<ul className="space-y-2 sm:space-y-3">
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Refresher with powerful filters
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										"Post a Truck" delete reminder
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Quick "PAT" order deletion
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Delivery-time anomaly warnings
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Hiding promoted loads
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Detailed load view
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Quick "Post a Truck" creation
									</span>
								</li>
							</ul>
						</div>

						<div className="p-4 sm:p-6 pt-0">
							<a
								href="https://chromewebstore.google.com/u/1/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb?hl=en"
								target="_blank"
								rel="noopener noreferrer"
								className="block w-full py-2 px-4 border border-gray-300 text-gray-700 rounded-full hover:bg-gray-50 transition-colors text-center font-medium"
							>
								Try it for free
							</a>
						</div>
					</div>

					{/* Plus Plan */}
					<div className="border border-gray-200 rounded-lg bg-white shadow-sm">
						<div className="p-4 sm:p-6 pb-2">
							<h3 className="text-lg sm:text-xl font-semibold text-gray-900">
								Plus
							</h3>
							<div className="flex items-baseline">
								<span className="text-3xl sm:text-4xl font-bold text-gray-900">
									{isYearly ? '$196' : '$24'}
								</span>
								<span className="text-sm sm:text-base text-gray-600 ml-1">
									/per {isYearly ? 'year' : 'month'}
								</span>
							</div>
							{isYearly && (
								<p className="text-xs text-green-600 mt-1">
									Save $92 (-32%)
								</p>
							)}
							<p className="text-sm sm:text-base text-gray-600 mt-2">
								For growing operations.
							</p>
						</div>

						<div className="px-4 sm:px-6 pb-4 sm:pb-6">
							<div className="text-xs sm:text-sm text-gray-600 font-medium mb-3">
								Everything in Free and:
							</div>
							<ul className="space-y-2 sm:space-y-3">
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Extension 5 devices/browsers
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Web app 1 device/browser
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Financials & driver earnings
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										In-Page trip notifications
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Load analytics & backhaul
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Telegram notifications
									</span>
								</li>
							</ul>
						</div>

						<div className="p-4 sm:p-6 pt-0">
							<a
								href="https://chromewebstore.google.com/u/1/detail/relay-auto-refresher-post/ihcgicdogclbieclnldfjmlglkolankb?hl=en"
								target="_blank"
								rel="noopener noreferrer"
								className="block w-full py-2 px-4 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-center font-medium"
							>
								Get Plus
							</a>
						</div>
					</div>

					{/* Pro Plan */}
					<div className="border border-gray-200 rounded-lg bg-white shadow-sm">
						<div className="p-4 sm:p-6 pb-2">
							<h3 className="text-lg sm:text-xl font-semibold text-gray-900">
								Pro
							</h3>
							<div className="flex items-baseline">
								<span className="text-3xl sm:text-4xl font-bold text-gray-900">
									{isYearly ? '$399' : '$49'}
								</span>
								<span className="text-sm sm:text-base text-gray-600 ml-1">
									/per {isYearly ? 'year' : 'month'}
								</span>
							</div>
							{isYearly && (
								<p className="text-xs text-green-600 mt-1">
									Save $189 (-32%)
								</p>
							)}
							<p className="text-sm sm:text-base text-gray-600 mt-2">
								Advanced tools for pros.
							</p>
						</div>

						<div className="px-4 sm:px-6 pb-4 sm:pb-6">
							<div className="text-xs sm:text-sm text-gray-600 font-medium mb-3">
								Everything in Plus and:
							</div>
							<ul className="space-y-2 sm:space-y-3">
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Extension 20 devices/browsers
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Web app 3 devices/browsers
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Telegram trip notifications
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Extended statistics & insights
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										"PAT" creation via website
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Safety tracking (coming soon)
									</span>
								</li>
							</ul>
						</div>

						<div className="p-4 sm:p-6 pt-0">
							<a
								href="/account/signup"
								className="block w-full py-2 px-4 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors text-center font-medium"
							>
								Get Pro
							</a>
						</div>
					</div>

					{/* Team Plan */}
					<div className="border border-gray-200 rounded-lg bg-white shadow-sm">
						<div className="p-4 sm:p-6 pb-2">
							<h3 className="text-lg sm:text-xl font-semibold text-gray-900">
								Team
							</h3>
							<div className="flex items-baseline justify-center gap-2">
								<span className="text-3xl sm:text-4xl font-bold text-gray-900">
									${isYearly ? teamSeats * 399 : teamSeats * 45}
								</span>
								<span className="text-sm sm:text-base text-gray-600">
									/{isYearly ? 'year' : 'month'}
								</span>
								<span className="text-sm text-gray-500">
									${isYearly ? '399' : '45'}/seat
								</span>
							</div>
							{isYearly && (
								<p className="text-xs text-green-600 mt-1">
									Save ${teamSeats * 141} (-26%)
								</p>
							)}

							{/* Seat Selector */}
							<div className="mt-3 flex items-center justify-center gap-3">
								<label className="text-sm font-medium text-gray-700">
									Seats
								</label>
								<div className="flex items-center gap-2">
									<button
										onClick={() => setTeamSeats(Math.max(2, teamSeats - 1))}
										disabled={teamSeats <= 2}
										className={`w-8 h-8 rounded-md flex items-center justify-center text-lg font-bold transition-colors ${
											teamSeats <= 2
												? 'bg-gray-200 text-gray-400 cursor-not-allowed'
												: 'bg-gray-200 text-gray-900 hover:bg-gray-300'
										}`}
									>
										−
									</button>
									<span className="text-xl font-bold w-8 text-center text-gray-900">
										{teamSeats}
									</span>
									<button
										onClick={() => setTeamSeats(Math.min(5, teamSeats + 1))}
										disabled={teamSeats >= 5}
										className={`w-8 h-8 rounded-md flex items-center justify-center text-lg font-bold transition-colors ${
											teamSeats >= 5
												? 'bg-gray-200 text-gray-400 cursor-not-allowed'
												: 'bg-gray-200 text-gray-900 hover:bg-gray-300'
										}`}
									>
										+
									</button>
								</div>
							</div>
						</div>

						<div className="px-4 sm:px-6 pb-4 sm:pb-6">
							<div className="text-xs sm:text-sm text-gray-600 font-medium mb-3">
								Everything in Pro and:
							</div>
							<ul className="space-y-2 sm:space-y-3">
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Extension {teamSeats * 20} devices/browsers
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Web app {teamSeats * 3} devices/browsers
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Shared team workspace
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Invite team members
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Centralized billing
									</span>
								</li>
								<li className="flex items-start gap-3">
									<div className="bg-blue-600 rounded-full p-1 flex-shrink-0">
										<Check className="h-3 w-3 text-white" />
									</div>
									<span className="text-gray-700 text-xs sm:text-sm">
										Flexible seat management
									</span>
								</li>
							</ul>
						</div>

						<div className="p-4 sm:p-6 pt-0">
							<a
								href="/account/signup"
								className="block w-full py-2 px-4 bg-purple-600 text-white rounded-full hover:bg-purple-700 transition-colors text-center font-medium"
							>
								Get Team
							</a>
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
